/**
 * Shared helper functions for SEO strategies
 * Reduces code duplication across strategy implementations
 */

import { CachedContent } from '../../../../interfaces/cached-content';
import { Seo, SeoMeta } from '../../../../interfaces/seo';
import { appendMetaValues } from '../append-meta-values';
import { applyDefaults, extractSeoConfig } from '../dynamic-seo-generator';
import { generateSchemaForPage, SchemaGeneratorConfig } from '../schema-generators';
import { setFallbackTitle } from './fallback-title.util';

/**
 * Helper for simple/static page strategies (FAQ, Help, Installation, etc.)
 * Applies SEO from a template in the JSON without dynamic data
 *
 * @param seoKey - Key in seo_en.json (e.g., 'faq', 'help', 'home')
 * @param pageName - Page name for image generation (e.g., 'faq', 'help')
 * @param currentPath - Current route path (e.g., '/faq', '/contact-us')
 * @param seoContent - SEO templates from JSON
 * @param document - DOM document to modify
 * @param cacheContent - Cached application data
 */
export function applySimplePageSeo(
  seoKey: string,
  pageName: string,
  currentPath: string,
  seoContent: Seo,
  document: Document,
  cacheContent: CachedContent
): void {
  const template = seoContent[seoKey];

  if (!template) {
    setFallbackTitle(cacheContent, document);
    return;
  }

  const config = extractSeoConfig(cacheContent);
  const finalSeo = applyDefaults(template, config, pageName, currentPath);

  // Generate schema markup if not provided in template
  if (!finalSeo.schemaMarkup) {
    const schemaConfig: SchemaGeneratorConfig = {
      customerName: config.customerName,
      baseUrl: config.baseUrl,
      logoUrl: config.logoUrl,
      contactEmail: config.contactEmail,
    };

    finalSeo.schemaMarkup = generateSchemaForPage(
      pageName,
      schemaConfig,
      finalSeo.title || '',
      finalSeo.description || '',
      currentPath
    );
  }

  appendMetaValues(finalSeo, cacheContent, document, undefined, pageName);
}

/**
 * Location finder function type for dynamic pages
 */
export type LocationFinder = (
  cache: CachedContent,
  id: string
) => { name: string; data: unknown } | null;

/**
 * SEO generator function type for dynamic pages
 */
export type SeoGenerator = (cache: CachedContent, id: string, template?: SeoMeta) => SeoMeta;

/**
 * Configuration for location-based SEO strategies
 */
export interface LocationSeoConfig {
  /** Key in seo_en.json (e.g., 'countryPage', 'regionPage') */
  seoKey: string;
  /** Page name for image generation (e.g., 'country', 'region') */
  pageName: string;
  /** Route path prefix (e.g., '/buy-plan/plans/country') */
  pathPrefix: string;
  /** Function to find location data in cache */
  locationFinder: LocationFinder;
  /** Function to generate dynamic SEO from cache */
  seoGenerator: SeoGenerator;
}

/**
 * Helper for dynamic location-based strategies (Country, Region, Global)
 * Generates SEO dynamically from cache data
 *
 * @param locationId - ID of the location (country/region/global ID)
 * @param config - Configuration object with SEO generation settings
 * @param seoContent - SEO templates from JSON
 * @param document - DOM document to modify
 * @param cacheContent - Cached application data
 */
export function applyLocationSeo(
  locationId: string | undefined,
  config: LocationSeoConfig,
  seoContent: Seo,
  document: Document,
  cacheContent: CachedContent
): void {
  if (!locationId) {
    setFallbackTitle(cacheContent, document);
    return;
  }

  const location = config.locationFinder(cacheContent, locationId);

  if (!location) {
    setFallbackTitle(cacheContent, document);
    return;
  }

  const template = seoContent[config.seoKey];
  const dynamicSeo = config.seoGenerator(cacheContent, locationId, template);

  const seoConfig = extractSeoConfig(cacheContent);
  const currentPath = `${config.pathPrefix}/${locationId}`;
  const finalSeo = applyDefaults(dynamicSeo, seoConfig, config.pageName, currentPath);

  appendMetaValues(finalSeo, cacheContent, document, location.name, config.pageName);
}
